import type { DragEvent, ReactNode } from "react";

import { groupArtIconSrc, groupBadgeIconSrc, portraitSrc } from "../assets/media";

export function WashiTape({ className = "" }: { className?: string }) {
  return <span className={`washi-tape ${className}`.trim()} aria-hidden="true" />;
}

export function PanelMascot({
  src,
  side = "right",
  className = ""
}: {
  src: string;
  side?: "left" | "right";
  className?: string;
}) {
  return (
    <img
      className={`panel-mascot panel-mascot-${side} ${className}`.trim()}
      src={src}
      alt=""
      aria-hidden="true"
    />
  );
}

type PodiumEntry = {
  id: number | string;
  name: string;
  score: number;
  faceSrc?: string;
};

export function Podium({
  entries,
  emptyTitle = "暂无上榜",
  emptyDetail = "记分后会出现领奖台。"
}: {
  entries: PodiumEntry[];
  emptyTitle?: string;
  emptyDetail?: string;
}) {
  const top = entries.slice(0, 3);
  const ordered = [top[1], top[0], top[2]].filter(Boolean);
  if (!top.length) {
    return (
      <div className="podium-scene empty">
        <p className="modal-detail">{emptyTitle}</p>
        <small>{emptyDetail}</small>
      </div>
    );
  }
  return (
    <div className="podium-scene">
      <div className="points-podium">
        {ordered.map((entry) => {
          const place = top.findIndex((item) => item.id === entry.id) + 1;
          return (
            <article key={entry.id} className={`points-podium-step step-${place}`}>
              <img
                className="podium-face"
                src={entry.faceSrc || portraitSrc(Number(entry.id) || 1)}
                alt=""
              />
              <strong>{entry.name}</strong>
              <b>{entry.score}</b>
              <span className="podium-place">{place}</span>
            </article>
          );
        })}
      </div>
    </div>
  );
}

type RaceEntry = {
  id: number;
  name: string;
  total: number;
  color?: string;
  count?: number;
  avg?: number;
};

export function RaceTrackList({
  groups,
  actions,
  onDragOver,
  onDragLeave,
  onDrop
}: {
  groups: RaceEntry[];
  actions?: (group: RaceEntry, index: number) => ReactNode;
  onDragOver?: (event: DragEvent<HTMLElement>) => void;
  onDragLeave?: (event: DragEvent<HTMLElement>) => void;
  onDrop?: (event: DragEvent<HTMLElement>, groupId: number) => void;
}) {
  const maxTotal = Math.max(1, ...groups.map((group) => Number(group.total || 0)));
  return (
    <div className="race-track-list">
      {groups.map((group, index) => {
        const pct = Math.max(10, Math.round((Number(group.total || 0) / maxTotal) * 100));
        return (
          <article
            className="race-track-row"
            key={group.id}
            data-group-id={group.id}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop ? (event) => onDrop(event, group.id) : undefined}
          >
            <span className="race-rank" style={{ background: group.color || "#55c86f" }}>
              {index + 1}
            </span>
            <img className="race-badge" src={groupBadgeIconSrc(index)} alt="" />
            <div className="race-meta">
              <div className="race-top">
                <strong>{group.name}</strong>
                <b>{group.total}</b>
              </div>
              <div className="race-lane" aria-hidden="true">
                <span className="race-lane-fill" style={{ width: `${pct}%` }} />
                <img
                  className="race-car"
                  src={groupArtIconSrc(index)}
                  alt=""
                  style={{ left: `calc(${pct}% - 18px)` }}
                />
                <span className="race-flag" />
              </div>
              {group.count != null ? (
                <small>
                  {group.count} 人{group.avg != null ? ` · 人均 ${group.avg}` : ""}
                </small>
              ) : null}
            </div>
            {actions ? <div className="race-actions">{actions(group, index)}</div> : null}
          </article>
        );
      })}
    </div>
  );
}
